import { ChevronLeft, ChevronRight } from "lucide-react";
import { addDays, monthLong, sameDay, startOfDay, weekdayShort } from "@/lib/calendar";
import { cn } from "@/lib/utils";

type Dots = 0 | 1 | 2 | 3 | null;

function DotRow({ n, light }: { n: Dots; light?: boolean }) {
  return (
    <span aria-hidden className="flex h-[3px] items-center gap-[3px]">
      {Array.from({ length: n ?? 0 }, (_, i) => (
        <span key={i} className={cn("h-[3px] w-[3px] rounded-full", light ? "bg-white/80" : "bg-primary")} />
      ))}
    </span>
  );
}

/**
 * "Pick a day": the month, Sunday first. Days before today are out; dots show how much
 * free time each day has (3 = lots), so a good day is visible before it's picked.
 */
export function DayPicker({
  month,
  onMonth,
  selected,
  onSelect,
  today,
  dotsFor,
  checking,
}: {
  /** Any date in the month shown. */
  month: Date;
  onMonth: (m: Date) => void;
  selected: Date;
  onSelect: (d: Date) => void;
  today: Date;
  /** null while unknown or when free/busy isn't available. */
  dotsFor: (d: Date) => Dots;
  checking: boolean;
}) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells: (Date | null)[] = [
    ...Array.from({ length: first.getDay() }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(month.getFullYear(), month.getMonth(), i + 1)),
  ];
  const atCurrentMonth = first <= new Date(today.getFullYear(), today.getMonth(), 1);
  const anyDots = cells.some((d) => d && (dotsFor(d) ?? 0) > 0);

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="text-[17px] font-semibold text-foreground">Pick a day</h2>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Previous month"
            disabled={atCurrentMonth}
            onClick={() => onMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
            className="flex h-8 w-8 items-center justify-center rounded-full text-foreground/70 hover:bg-foreground/[0.05] disabled:opacity-30"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="min-w-[118px] text-center text-[15px] font-medium text-foreground">
            {monthLong(month.getMonth())} {month.getFullYear()}
          </span>
          <button
            type="button"
            aria-label="Next month"
            onClick={() => onMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
            className="flex h-8 w-8 items-center justify-center rounded-full text-foreground/70 hover:bg-foreground/[0.05]"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div role="grid" aria-label={`${monthLong(month.getMonth())} ${month.getFullYear()}`} className="mt-3 grid grid-cols-7 gap-y-1">
        {[0, 1, 2, 3, 4, 5, 6].map((d) => (
          <span key={d} role="columnheader" className="pb-2 text-center text-[11.5px] text-muted-foreground">
            {weekdayShort(d)}
          </span>
        ))}
        {cells.map((d, i) => {
          if (!d) return <span key={`blank-${i}`} />;
          const past = d < startOfDay(today);
          const isSelected = sameDay(d, selected);
          const isToday = sameDay(d, today);
          const dots = past ? 0 : dotsFor(d);
          return (
            <div key={d.getDate()} className="flex flex-col items-center gap-[3px] py-0.5">
              <button
                type="button"
                role="gridcell"
                aria-selected={isSelected}
                aria-label={`${weekdayShort(d.getDay())} ${monthLong(d.getMonth())} ${d.getDate()}${dots ? `, ${["", "a little", "some", "lots of"][dots]} free time` : ""}`}
                disabled={past}
                onClick={() => onSelect(d)}
                className={cn(
                  "flex h-[34px] w-[34px] items-center justify-center rounded-full text-[14px] tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                  past && "text-foreground/30",
                  !past && !isSelected && "text-foreground hover:bg-foreground/[0.06]",
                  !past && !isSelected && (dots ?? 0) >= 2 && "bg-primary/[0.09] font-medium",
                  isToday && !isSelected && "ring-1 ring-inset ring-foreground/35",
                  isSelected && "bg-foreground font-semibold text-white",
                )}
              >
                {d.getDate()}
              </button>
              <DotRow n={dots} />
            </div>
          );
        })}
      </div>
      <p className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
        {checking ? (
          "Checking free time…"
        ) : anyDots ? (
          <>
            <DotRow n={3} /> More free time
          </>
        ) : null}
      </p>
    </div>
  );
}

/** Phones: the next three weeks as a strip, free time as dots. */
export function MobileDayStrip({
  selected,
  onSelect,
  today,
  dotsFor,
}: {
  selected: Date;
  onSelect: (d: Date) => void;
  today: Date;
  dotsFor: (d: Date) => Dots;
}) {
  const days = Array.from({ length: 21 }, (_, i) => addDays(startOfDay(today), i));
  return (
    <div className="-mx-4 flex snap-x gap-1.5 overflow-x-auto px-4 pb-1 [scrollbar-width:none]">
      {days.map((d) => {
        const active = sameDay(d, selected);
        return (
          <button
            key={d.toISOString()}
            type="button"
            aria-pressed={active}
            onClick={() => onSelect(d)}
            className={cn(
              "flex w-11 shrink-0 snap-start flex-col items-center gap-1 rounded-[14px] py-2 transition-colors",
              active ? "bg-foreground text-white" : "text-foreground hover:bg-foreground/[0.05]",
            )}
          >
            <span className={cn("text-[11px]", active ? "text-white/75" : "text-muted-foreground")}>{weekdayShort(d.getDay())}</span>
            <span className="text-[15px] font-medium tabular-nums">{d.getDate()}</span>
            <DotRow n={dotsFor(d)} light={active} />
          </button>
        );
      })}
    </div>
  );
}
